/**
 * SpinningSun — two-layer kawaii sun used in the home/profile/dictionary
 * backgrounds. Web port of client-native SpinningSun.tsx.
 *
 * `sunfull2.png` (the rays) rotates slowly beneath the STATIC `sunface.png`
 * face. This matches iOS exactly: only the ray halo spins, the smiling face
 * stays still. (The previous PWA Home spun the combined sunfull2.png, so the
 * face spun too — the bug this component fixes.)
 *
 * The rotating layer + face are stacked inside a square box; the caller
 * positions/opacity-styles that box via `style`.
 */
import { CSSProperties, FC } from "react";
import sunRays from "../../assets/profile/sunfull2.png";
import sunFace from "../../assets/profile/sunface.png";

interface SpinningSunProps {
  /** Square edge length. A number is treated as px; a string is used as-is so
   *  callers can pass responsive CSS like `clamp(90px, 38vw, 150px)`. */
  size: number | string;
  /** Rotation period in seconds (iOS Home ≈ slow continuous spin). */
  spinSeconds?: number;
  /** Caller-supplied positioning/opacity. `position: absolute` is set here. */
  style?: CSSProperties;
}

const layer: CSSProperties = {
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  objectFit: "contain",
};

const SpinningSun: FC<SpinningSunProps> = ({ size, spinSeconds = 18, style }) => (
  <div
    aria-hidden
    style={{ position: "absolute", width: size, height: size, ...style }}
  >
    {/* keyframes for the gentle ray spin (face layer is intentionally static) */}
    <style>{`@keyframes parrot-sun-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    <img
      src={sunRays}
      alt=""
      aria-hidden
      style={{ ...layer, animation: `parrot-sun-spin ${spinSeconds}s linear infinite` }}
    />
    <img src={sunFace} alt="" aria-hidden style={layer} />
  </div>
);

export default SpinningSun;
